import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Key, Copy, EnvelopeSimple, Check, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Coach/admin one-tap password reset for a user.
 *
 * Renders an icon trigger and a confirmation dialog. On confirm, calls
 * `POST /api/users/{id}/reset-password` to generate a fresh password.
 * The plaintext is shown ONCE, with Copy + (optional) Email-it buttons.
 */
export default function ResetPasswordButton({ userId, userName, userEmail, trigger }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("confirm"); // confirm | result
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { new_password, email_status }
  const [copied, setCopied] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const reset = (sendEmail) => async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${userId}/reset-password`, { send_email: sendEmail });
      setResult(data);
      setStep("result");
      if (sendEmail) {
        if (data.email_status === "sent") toast.success("Password sent by email");
        else toast.warning("Password reset, but email could not be sent");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      setEmailing(false);
    }
  };

  const sendEmail = async () => {
    if (!result) return;
    setEmailing(true);
    try {
      const { data } = await api.post(`/users/${userId}/reset-password`, { send_email: true });
      // Replace shown password (since we just rotated again) so coach has the
      // currently-active one to share if email delivery is gated.
      setResult(data);
      if (data.email_status === "sent") toast.success("Password sent by email");
      else toast.warning("Email could not be sent — share the password below manually");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailing(false);
    }
  };

  const copy = async () => {
    if (!result?.new_password) return;
    try {
      await navigator.clipboard.writeText(result.new_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't access clipboard — select the password and copy manually");
    }
  };

  const close = () => {
    setOpen(false);
    // Tiny delay so the dialog finishes its close animation before state resets.
    setTimeout(() => {
      setStep("confirm");
      setResult(null);
      setCopied(false);
    }, 200);
  };

  const Trigger = trigger || (
    <Button size="sm" variant="ghost" className="rounded-lg" title="Reset password"
            aria-label={`Reset password for ${userName}`}>
      <Key size={14} className="text-ink-2" />
    </Button>
  );

  return (
    <>
      <span onClick={() => setOpen(true)} className="inline-block" data-testid={`reset-password-${userId}`}>
        {Trigger}
      </span>
      <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="rounded-2xl max-w-md">
          {step === "confirm" && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Key size={18} weight="fill" className="text-terracotta" />
                  Reset password
                </DialogTitle>
                <DialogDescription>
                  Generate a fresh password for <b>{userName}</b>. Their current password
                  will stop working immediately.
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-xl bg-warning/10 border border-warning/30 p-3 text-sm flex items-start gap-2">
                <Warning size={16} weight="fill" className="text-warning shrink-0 mt-0.5" />
                <span>You'll see the new password on the next screen — it won't be shown again.</span>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="ghost" className="rounded-xl" onClick={close}>Cancel</Button>
                <Button
                  onClick={reset(false)}
                  disabled={busy}
                  className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white"
                  data-testid="reset-password-confirm"
                >
                  {busy ? "Generating…" : "Generate new password"}
                </Button>
              </DialogFooter>
            </>
          )}

          {step === "result" && result && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Check size={18} weight="bold" className="text-moss" />
                  New password for {userName}
                </DialogTitle>
                <DialogDescription>
                  Send this to {userEmail || "the user"}. They can change it once they log in.
                </DialogDescription>
              </DialogHeader>

              <div className="rounded-xl bg-muted2 border border-line p-4">
                <div className="hs-label text-[10px]">Password</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <code className="flex-1 font-data text-lg font-semibold text-ink tracking-wider select-all"
                        data-testid="reset-password-value">
                    {result.new_password}
                  </code>
                  <Button onClick={copy} size="sm" variant="secondary" className="rounded-lg shrink-0"
                          data-testid="reset-password-copy">
                    {copied ? <Check size={14} weight="bold" /> : <Copy size={14} />}
                    <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
                  </Button>
                </div>
              </div>

              {userEmail && (
                <Button
                  onClick={sendEmail}
                  disabled={emailing}
                  variant="secondary"
                  className="rounded-xl w-full"
                  data-testid="reset-password-email"
                >
                  <EnvelopeSimple size={14} className="mr-2" />
                  {emailing
                    ? "Sending…"
                    : result.email_status === "sent"
                      ? "Email it again"
                      : `Email it to ${userEmail}`}
                </Button>
              )}

              <DialogFooter>
                <Button className="rounded-xl bg-ink hover:bg-ink/90 text-white hover:text-white"
                        onClick={close}
                        data-testid="reset-password-close">
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
